import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Drawer,
  Grid,
  Icon,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";

import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { setNotificationMessage, useMaterialUIController } from "context";

import { IMessenger } from "interfaces/messenger";
import {
  deleteMessenger,
  getAllMessengers,
  updateStatusMessanger,
} from "services/messenger";

import SearchMessengers from "./components/SearchMessengers";
import ModalMessenger from "./components/ModalMessenger";

import { debouce } from "helpers/utils";
import { formatDateEndOfDay } from "helpers/formatDate";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

export default function Messenger() {
  const theme = useTheme();
  const [, dispatch] = useMaterialUIController();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [listMessenger, setListMessenger] = useState<IMessenger[]>([]);
  const [searchListMessenger, setSearchListMessenger] = useState<
    IMessenger[] | null
  >(null);
  const [selectedMessenger, setSelectedMessenger] = useState<IMessenger | null>(
    null
  );

  const displayRows = searchListMessenger ?? listMessenger;
  const unreadCount = useMemo(
    () => listMessenger.filter((item) => item.status === false).length,
    [listMessenger]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getAllMessengers().finally(() =>
        setIsLoading(false)
      );
      if (result.status === 200) {
        const messagNotViewed = result.data.filter(
          (item) => item.status === false
        ).length;
        setListMessenger(result.data);
        setNotificationMessage(dispatch, messagNotViewed);
        setSearchListMessenger(null);
      }
    })();
  }, [dispatch]);

  const handlerOnChangeSearch = (searchTerm: string) => {
    if (!searchTerm) {
      setSearchListMessenger(null);
      return;
    }

    const filteredData = listMessenger.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setSearchListMessenger(filteredData);
  };

  const debouncedHandler = useMemo(
    () => debouce(handlerOnChangeSearch, 400),
    [listMessenger]
  );

  const handleCloseDetail = () => {
    setDetailOpen(false);
  };

  const handleDeleteMessanger = async (idMsg: number) => {
    const result = await deleteMessenger(idMsg);
    if (result.status === 200) {
      const next = listMessenger.filter((item) => item.id !== idMsg);
      setListMessenger(next);
      setNotificationMessage(
        dispatch,
        next.filter((item) => item.status === false).length
      );
    }
    setDetailOpen(false);
    setSelectedMessenger(null);
  };

  const columns: GridColDef<IMessenger>[] = [
    {
      field: "status",
      headerName: "Leitura",
      width: 130,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.5}
          onClick={(e) => e.stopPropagation()}
        >
          {params.row.status ? (
            <>
              <Chip
                size="small"
                icon={<MarkEmailReadOutlinedIcon />}
                label="Lida"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, p: 1.5 }}
              />
              <IconButton
                size="small"
                aria-label="Excluir"
                onClick={() => handleDeleteMessanger(params.row.id)}
              >
                <Icon color="error" fontSize="small">
                  delete
                </Icon>
              </IconButton>
            </>
          ) : (
            <Chip
              size="small"
              icon={<MarkEmailUnreadOutlinedIcon />}
              label="Nova"
              color="warning"
              variant="filled"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
      ),
    },
    {
      field: "createAt",
      headerName: "Data",
      width: 118,
      align: "left",
      valueGetter: (params) => formatDateEndOfDay(params.row.createAt),
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 0.9,
      minWidth: 160,
      align: "left",
      valueGetter: (params) => params.row.nome,
    },
    {
      field: "empresa",
      headerName: "Empresa",
      flex: 0.7,
      minWidth: 130,
      align: "left",
      valueGetter: (params) => params.row.empresa,
    },
    {
      field: "equipe",
      headerName: "Equipe",
      width: 120,
      align: "left",
      valueGetter: (params) => params.row.equipe,
    },
    {
      field: "email",
      headerName: "E-mail",
      flex: 1,
      minWidth: 200,
      align: "left",
      valueGetter: (params) => params.row.email,
    },
    {
      field: "phoneCel",
      headerName: "Celular",
      width: 130,
      align: "left",
      valueGetter: (params) => params.row.phoneCel,
    },
    {
      field: "message",
      headerName: "Mensagem",
      flex: 1,
      minWidth: 200,
      align: "left",
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
            py: 1,
          }}
          title={params.row.message}
        >
          {params.row.message}
        </Typography>
      ),
    },
  ];

  const handleSelectedMessenger = (messenger: IMessenger) => {
    if (messenger.status) {
      setSelectedMessenger(messenger);
      setDetailOpen(true);
    } else {
      updateStatusMessanger(messenger.id).then((resp) => {
        const upDataMsg = [
          ...listMessenger.filter((item) => item.id !== resp.data.id),
          resp.data,
        ];
        setListMessenger(upDataMsg);
        setSelectedMessenger(resp.data);
        setNotificationMessage(
          dispatch,
          upDataMsg.filter((item) => item.status === false).length
        );
        setDetailOpen(true);
      });
    }
  };

  const sortModel: GridSortModel = useMemo(
    () => [
      {
        field: "status",
        sort: "asc",
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={4}>
        <Card
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
            boxShadow: (t) =>
              `0 0 0 1px ${t.palette.divider}, 0 12px 40px ${alpha(
                t.palette.common.black,
                0.06
              )}`,
          }}
        >
          {/* Hero / cabeçalho da página */}
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 2.5, md: 3 },
              background: `linear-gradient(125deg, ${alpha(
                theme.palette.primary.main,
                0.14
              )} 0%, ${alpha(theme.palette.primary.light, 0.1)} 55%, ${alpha(
                theme.palette.background.paper,
                1
              )} 100%)`,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    color: "primary.main",
                  }}
                >
                  <InboxOutlinedIcon />
                </Box>
                <Box>
                  <MDTypography variant="h4" fontWeight="bold">
                    Caixa de mensagens
                  </MDTypography>
                  <Typography variant="body2" color="text.secondary">
                    Contatos enviados pelo site — clique em uma linha para ler e
                    responder.
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={`${listMessenger.length} total`}
                  variant="outlined"
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                />
                <Chip
                  label={`${unreadCount} não lidas`}
                  color={unreadCount > 0 ? "warning" : "default"}
                  variant={unreadCount > 0 ? "filled" : "outlined"}
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                />
              </Stack>
            </Stack>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 }, pt: 2.5 }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} md={8} lg={6}>
                <SearchMessengers onChange={debouncedHandler} />
              </Grid>
            </Grid>

            <DataGrid
              sx={{
                flexGrow: 1,
                border: "none",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: alpha(theme.palette.grey[500], 0.08),
                  borderRadius: "12px 12px 0 0",
                  fontWeight: 700,
                },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                },
                "& .MuiDataGrid-row:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
                "& .messenger-row-unread": {
                  bgcolor: alpha(theme.palette.warning.main, 0.06),
                  fontWeight: 500,
                },
                "& .messenger-row-unread:hover": {
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                },
                "& .MuiDataGrid-cell": {
                  borderColor: "divider",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderColor: "divider",
                },
              }}
              columnHeaderHeight={44}
              rows={displayRows}
              columns={columns}
              loading={isLoading}
              autoHeight
              localeText={{
                noRowsLabel: searchListMessenger
                  ? "Nenhum resultado para sua busca."
                  : "Nenhuma mensagem recebida ainda.",
              }}
              onRowClick={(params) =>
                handleSelectedMessenger(params.row as IMessenger)
              }
              density="comfortable"
              pageSizeOptions={[10, 20, 50, 100]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
              }}
              sortModel={sortModel}
              getRowClassName={(params) =>
                !params.row.status ? "messenger-row-unread" : ""
              }
            />
          </CardContent>
        </Card>

        <Drawer
          anchor="right"
          open={detailOpen}
          onClose={handleCloseDetail}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: "50vw" },
              maxWidth: { xs: "100vw", sm: "50vw" },
              boxShadow: (t) =>
                `-12px 0 40px ${alpha(t.palette.common.black, 0.12)}`,
            },
          }}
        >
          {selectedMessenger && (
            <ModalMessenger
              messenger={selectedMessenger}
              deleteMessenger={handleDeleteMessanger}
              onClose={handleCloseDetail}
            />
          )}
        </Drawer>
      </MDBox>
    </DashboardLayout>
  );
}
